/**
 * One chart group at a time, on a ring.
 *
 * The mobile answer to a layout that holds several charts. Every group is
 * rendered as a page; one is on screen and the rest wait off either edge, and
 * the Chart tab's own tap moves between them (see `MobileTabBar`). The arithmetic
 * is `lib/chart-pager.ts` — pure, and tested there, because nothing that touches
 * a SciChart surface can be tested at all.
 *
 * ## Every page stays mounted, and none is ever `display: none`
 *
 * A hidden subtree has no geometry, so SciChart measures a zero-height container
 * and lays out wrong on the way back — the trap `charts.md` documents for the
 * div SciChart mutates on `delete()`, arriving by a second route. Pages are
 * therefore moved with `transform`, which changes nothing about their box.
 *
 * Unmounting the off-screen ones was the other option and is worse than it
 * looks: a remount is a WebGL teardown plus a refetch, so a lap of two charts
 * would cost four. N live surfaces is what a desktop multi-chart layout already
 * costs, and this shows fewer of them at once, not more.
 *
 * ## The wrap is a teleport, not a longer journey
 *
 * Going from the last chart back to the first must look like every other step.
 * The page arriving is placed on the side it enters from **with transitions
 * suppressed, while it is off screen** (`enterFrom`), and only then animated in.
 * Without that, the incoming page travels backwards across the whole screen.
 *
 * ## All: the same pages, laid out on the layout's grid
 *
 * The iPad's other mode (`docs/features/mobile.md`, "iPad: All and Full"). The
 * stage becomes a CSS grid and each page drops its transform and takes its cell,
 * so going between the modes moves boxes and never remounts a surface. A tap on a
 * page selects it, captured on the way down so the same touch still pans.
 */

import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import type { Annotation, ChartGroup } from "@qufolio/model";

import { ChartGroupView, type GoToDate } from "./ChartGroupView";
import { enterFrom, ringOffsets } from "../lib/chart-pager";
import type { AnnotationTool } from "./AnnotationTools";

export interface ChartPagerProps {
  /** The layout's chart groups, in the order the layout lists them. */
  groups: readonly ChartGroup[];
  layoutId: string;
  /** Which one is on screen. Owned by the shell — the tab bar advances it. */
  index: number;
  /** Which direction the last change travelled, so the animation matches. */
  direction: 1 | -1;
  /** Rendered over the visible page — the symbol grip's wheel. */
  overlay?: ReactNode;
  onPaneHeights?: (chartGroupId: string, heights: string[]) => void;
  onWindow?: (chartGroupId: string, window: { visibleBars?: number; barsRange?: number }) => void;
  goTo?: GoToDate & { chartGroupId: string };
  drawing?: { chartGroupId: string; tool: AnnotationTool | null; trading?: string | null };
  journal?: {
    chartGroupId: string;
    annotations: readonly Annotation[];
    editable?: ReadonlySet<string>;
  };
  onLevelMoved?: (annotationId: string, price: number) => void;
  onLevelMenu?: (annotationId: string, x: number, y: number) => void;
  onAnnotationsDeleted?: (chartGroupId: string, annotationIds: readonly string[]) => void;
  onPlanDrawn?: (chartGroupId: string, entry: number, stop: number, at: number) => void;
  clearNonce?: number;
  onLastPrice?: (symbol: string, price: number, at: number) => void;
  onAnnotationDrawn?: (chartGroupId: string, paneId: string, annotation: Annotation) => void;
  /**
   * All mode: every page in its cell of the layout's grid, aligned with `groups`
   * (`lib/chart-grid.ts`). Absent ⇒ the ring, one page at a time.
   */
  all?: {
    rows: string;
    columns: string;
    cells: readonly ({ gridRow: string; gridColumn: string } | undefined)[];
  };
  /** A page was touched in All mode — it becomes the selected one. */
  onSelect?: (index: number) => void;
}

const STAGE: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  position: "relative",
  overflow: "hidden",
  background: "var(--qf-background)",
};

const SLIDE = "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)";

export function ChartPager({
  groups,
  layoutId,
  index,
  direction,
  overlay,
  onPaneHeights,
  onWindow,
  goTo,
  drawing,
  journal,
  onLevelMoved,
  onLevelMenu,
  onAnnotationsDeleted,
  onPlanDrawn,
  clearNonce,
  onLastPrice,
  onAnnotationDrawn,
  all,
  onSelect,
}: ChartPagerProps) {
  const pages = useRef<(HTMLDivElement | null)[]>([]);
  /** The index the DOM is currently showing, so a change knows what moved. */
  const shown = useRef(index);

  /**
   * Place the pages, teleporting the incoming one first.
   *
   * `useLayoutEffect` because the teleport and the animation are two writes that
   * must not be painted between: committed in an ordinary effect, the browser
   * has already drawn the incoming page at its old offset and the "suppressed"
   * transition is visible after all.
   */
  const allMode = !!all;
  useLayoutEffect(() => {
    if (allMode) {
      // Every page in its cell: no ring, nothing to animate. `shown` follows
      // the selection, so going back to Full does not slide in from a stale page.
      for (const page of pages.current) {
        if (!page) continue;
        page.style.transition = "none";
        page.style.transform = "none";
      }
      shown.current = index;
      return;
    }
    const offsets = ringOffsets(groups.length, index);
    const first = shown.current === index;

    if (!first) {
      // The page about to arrive, put where it must arrive from — with no
      // transition, and read back so the browser commits it before the next
      // write animates. `offsetHeight` is the flush; nothing uses its value.
      const incoming = pages.current[index];
      if (incoming) {
        incoming.style.transition = "none";
        incoming.style.transform = `translateX(${enterFrom(direction) * 100}%)`;
        void incoming.offsetHeight;
      }
    }

    offsets.forEach((offset, i) => {
      const page = pages.current[i];
      if (!page) return;
      // The first paint has nothing to animate from, and a layout that gains or
      // loses a chart must not slide the survivors around.
      page.style.transition = first ? "none" : SLIDE;
      page.style.transform = `translateX(${offset * 100}%)`;
    });

    shown.current = index;
  }, [groups.length, index, direction, allMode]);

  // A layout with no chart at all — a screener-only arrangement. The tab still
  // exists, so it says why rather than showing a black rectangle.
  if (groups.length === 0) {
    return (
      <div style={{ ...STAGE, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--qf-text-secondary)", fontSize: "0.8rem" }}>
          This layout has no chart.
        </span>
      </div>
    );
  }

  return (
    <div
      style={
        all
          ? { ...STAGE, display: "grid", gridTemplateRows: all.rows, gridTemplateColumns: all.columns, gap: 1 }
          : STAGE
      }
    >
      {groups.map((group, i) => (
        <div
          key={group.id}
          ref={(node) => {
            pages.current[i] = node;
          }}
          style={
            all
              ? {
                  position: "relative",
                  minWidth: 0,
                  minHeight: 0,
                  ...all.cells[i],
                }
              : {
                  position: "absolute",
                  inset: 0,
                  // Told to the compositor because these move on every tab tap and
                  // each one holds a canvas the size of the screen.
                  willChange: "transform",
                }
          }
          // Captured, not bubbled: a chart's own handlers may stop a pointer
          // event, and the touch that selects must still pan.
          onPointerDownCapture={all && onSelect ? () => onSelect(i) : undefined}
          // Off-screen pages are inert to a screen reader and to a stray tap
          // that lands past the edge mid-animation. In All every page is on screen.
          aria-hidden={!all && i !== index}
        >
          <ChartGroupView
            group={group}
            layoutId={layoutId}
            // The phone has no chart toolbar, so the badge stays on the chart.
            liveBadgeOnCanvas
            onPaneHeights={
              onPaneHeights ? (heights) => onPaneHeights(group.id, heights) : undefined
            }
            onWindow={onWindow ? (window) => onWindow(group.id, window) : undefined}
            goTo={goTo?.chartGroupId === group.id ? goTo : undefined}
            drawingTool={drawing?.chartGroupId === group.id ? drawing.tool : null}
            tradingTool={drawing?.chartGroupId === group.id ? (drawing.trading ?? null) : null}
            journalAnnotations={
              journal?.chartGroupId === group.id ? journal.annotations : undefined
            }
            journalEditable={journal?.chartGroupId === group.id ? journal.editable : undefined}
            onLevelMoved={onLevelMoved}
            onLevelMenu={onLevelMenu}
            onAnnotationsDeleted={
              onAnnotationsDeleted ? (ids) => onAnnotationsDeleted(group.id, ids) : undefined
            }
            onPlanDrawn={
              onPlanDrawn ? (entry, stop, at) => onPlanDrawn(group.id, entry, stop, at) : undefined
            }
            clearNonce={clearNonce}
            onLastPrice={onLastPrice}
            onAnnotationDrawn={
              onAnnotationDrawn
                ? (paneId, annotation) => onAnnotationDrawn(group.id, paneId, annotation)
                : undefined
            }
          />
          {/* The selection: a frame over the chart rather than an outline, which
              a WebGL canvas can paint over, and inside the box so the grid never
              moves. Not drawn when there is nothing else to choose. */}
          {all && i === index && groups.length > 1 && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                border: "2px solid var(--qf-accent)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            />
          )}
        </div>
      ))}
      {overlay}
    </div>
  );
}

/**
 * Keep an index inside a list that can shrink.
 *
 * A layout edited on the desktop can lose the chart this shell is sitting on,
 * and an index past the end renders nothing at all — the mobile equivalent of
 * `App`'s clamp on `layoutIndex`.
 */
export function useClampedIndex(count: number, index: number, onClamp: (next: number) => void) {
  useEffect(() => {
    if (count > 0 && index > count - 1) onClamp(count - 1);
  }, [count, index, onClamp]);
}
